import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TabPrimary, Button } from "@ds/ds-snam/react";
import { Container, Col, Row } from "react-grid-system";
import { useTranslation } from "react-i18next";
import { ICalendarDay, ICalendarDayService } from "dataModels/b2b";
import {
    getCalendarSettings,
    putCalendarSettings
} from "net/b2b";
import { v4 as uuid } from "uuid";
import { requestPending, requestPristine } from "state/reducer/requestStatusSlice";
import CalendarSettingsFormDataInput from "./calendarSettingsFormDataInput";
import { INotificationAction } from "dataModels/stateModels/notification";
import { showNotification, hideNotification } from "state/reducer/notificationSlice";
import { DSTimePicker } from "@ds/ds-snam";
import { transformTime } from "shared/utils/dateUtils";
import SystemError from "components/SystemError";
import { AppUserRole, DsLayout, IAppUserProfile, RoutePath } from "shared/constants";
import { RootState } from "state/appStore";
import { AppUser } from "ooAuth2/appUser";
import useAppUser from "hooks/useAppUser";



const CalendarSettingsForm: React.FC<{}> = (): JSX.Element => {

    const { t } = useTranslation(['b2b', 'app']);
    const [selectedDay, setSelectedDay] = useState<number>(0);
    const [formData, setFormData] = useState<ICalendarDay[] | undefined>();
    const [formError, setFormError] = useState<boolean>(false);
    const layoutMode = useSelector((state:RootState)=>{
        return state.dsLayout.mode;
    })
    const appUser: AppUser | undefined = useAppUser();
    const appUserRole: AppUserRole | undefined = appUser?.getRole();
    const appUserProfile: IAppUserProfile | undefined = appUser?.userProfile;
    const dispatch = useDispatch();

    const loadData = useCallback(() => {
        dispatch(requestPending());
        getCalendarSettings()
            .then((response: any) => {
                setFormData(response.data);
            })
            .catch(error => {
                console.log(error);
            })
            .finally(() => {
                dispatch(requestPristine());
            })
    }, [dispatch]);

    useEffect(() => {
        loadData();
        return () => {
            dispatch(requestPristine());
        }
    }, [loadData, dispatch]);


    const weekDays = (() => {
        return t("general.weekDays", { ns: 'app', returnObjects: true }) as string[];
    })();


    //Day start|end time change
    const onTimeInputChange = (value: string, fieldName: string) => {
        //console.log(fieldName, value);
        if (formData) {
            let currentFormData = [...formData];
            currentFormData[selectedDay][fieldName] = value;
            //Set also all services start/end time equal to start/end day time
            //and set time picker components limits
            currentFormData[selectedDay].services.forEach((serviceObj: ICalendarDayService) => {
                if (!serviceObj.available){
                    //serviceObj[fieldName] = value;
                    //if service time is < startTime or > endTime sets to start/end time ?
                    //otherwise picker val is set to null by default
                    const dayStartTime = transformTime(currentFormData[selectedDay].startTime);
                    const dayEndTime = transformTime(currentFormData[selectedDay].endTime);
                    const serviceStartTime = transformTime(serviceObj.startTime);
                    const serviceEndTime = transformTime(serviceObj.endTime);
                    //console.log(dayStartTime, serviceStartTime , serviceStartTime < dayStartTime)
                    //if (serviceStartTime < dayStartTime) {
                        serviceObj.startTime = currentFormData[selectedDay].startTime;
                    //}
                    //if (serviceEndTime > dayEndTime) {
                        serviceObj.endTime = currentFormData[selectedDay].endTime;
                    //}
                }
            });
            setFormData(currentFormData);
        }
    }


    //Day Service start|end time change
    const onServiceTimeInputChange = (value: string, fieldName: string, serviceType: number) => {
        if (formData) {
            let currentFormData = [...formData];
            currentFormData[selectedDay].services[serviceType][fieldName] = value;
            setFormData(currentFormData);
        }
    }

    const validateForm = (): number => {
        let errors: number = 0;
        //each rows query
        //console.log(document.querySelectorAll("div[class*='calendarSettingsFormStyles_row'"))

        //1.Check that ALL endTimes are > of relative start time
        const startTimeList: NodeList = document.querySelectorAll("ds-time-picker[id*='startTime']");
        const endTimeList: NodeList = document.querySelectorAll("ds-time-picker[id*='endTime']");
        startTimeList.forEach((element: Node, index: number) => {
            //console.log((element as DSTimePicker).defaultTime, (endTimeList[index] as DSTimePicker).defaultTime);
            const startPicker = (element as DSTimePicker);
            const endPicker = endTimeList[index] as DSTimePicker;
            const startTimeValue = transformTime(startPicker.defaultTime);
            const endTimeValue = transformTime(endPicker.defaultTime);
            const koCheck = (endTimeValue < startTimeValue);
            endPicker.timePickerStatus = koCheck ? "error" : "default";
            if (koCheck)
                errors++;
        });

        //2. Check that all start time are not < of day start time and all end times are not > of day end time
        document.querySelectorAll("div[class*='calendarSettingsFormStyles_row'").forEach((row: Element) => {
            const serviceStartPicker: DSTimePicker | null = row.querySelector("ds-time-picker[id='startTime-service']") as DSTimePicker;
            const serviceEndPicker: DSTimePicker | null = row.querySelector("ds-time-picker[id='endTime-service']") as DSTimePicker;

            //console.log(serviceStartPicker.defaultTime, formData![selectedDay].startTime, transformTime(serviceStartPicker.defaultTime), transformTime(formData![selectedDay].startTime));
            if (transformTime(serviceStartPicker.defaultTime) < transformTime(formData![selectedDay].startTime)) {
                serviceStartPicker.timePickerStatus = "error";
                errors++
            }
            //console.log("----")
            /*console.log(
                serviceEndPicker.defaultTime, 
                formData![selectedDay].endTime, 
                transformTime(serviceEndPicker.defaultTime), 
                transformTime(formData![selectedDay].endTime),
                transformTime(serviceEndPicker.defaultTime) > transformTime(formData![selectedDay].endTime)
            )*/
            if (transformTime(serviceEndPicker.defaultTime) > transformTime(formData![selectedDay].endTime)) {
                serviceEndPicker.timePickerStatus = "error";
                errors++
            }
        })
        return errors;

    }

    const saveFormData = useCallback(() => {
        let popUpAction: INotificationAction;
        dispatch(requestPending());
        putCalendarSettings(formData!)
            .then((response) => {
                popUpAction = {
                    show: true,
                    title: t(`calendarSettingsForm.successToast.title`),
                    description: t(`calendarSettingsForm.successToast.subTitle`),
                    status: "confirm",
                    onClose: (event: Event) => {
                        dispatch(hideNotification());
                    }
                };
                dispatch(showNotification(popUpAction));
            })
            .catch((error) => {
                console.log(error);
                /*popUpAction = {
                    show:true,
                    title: "Non è possibile completare l’azione",
                    description: "Si è verificato un errore durante l’inserimento del contratto. Riprova a caricare il file o contatta il nostro team di supporto.",
                    status: "error",
                    onClose: (event:Event)=>{
                        dispatch(hideNotification());
                    }
                }*/
                setFormError(true);//show error page
            })
            .finally(() => {
                dispatch(requestPristine());
            })
        //eslint-disable-next-line
    }, [formData, dispatch, t]);



    return (<>
        <div>
            {formError ? <SystemError link={RoutePath.B2B_CALENDAR} /> : <>
            
                <Row style={{marginBottom:'24px'}}>
                    <Col>
                        <p className="pageTitle">
                            {/*Configurazione*/}
                            {t('calendarSettingsForm.title')}
                        </p>
                        {<p className="pageSubTitle">
                           
                            {t('calendarSettingsForm.subTitle')}
                        </p>}
                    </Col>
                </Row>


                <div className="listTableContainerGrey" style={{marginRight:'-15px'}}>
                    <Row style={{marginBottom:'12px'}}>
                        <Col>
                            <p className="formSubTitle">
                                {/*Inserisci i parametri della configurazione del calendario*/}
                                {t('calendarSettingsForm.formTitle')}
                            </p>
                        </Col>
                    </Row>

                    <Row style={{marginBottom:'12px'}}>
                        <Col>
                            <div
                                style={{ display: "flex", flexDirection: "row", gap: "24px" }}
                                role="tablist"
                                aria-orientation="horizontal"
                            >{
                                    weekDays.map((weekDay: string, index: number) => {
                                        return (
                                            <TabPrimary
                                                key={uuid()}
                                                tabText={weekDay}
                                                tabOrientation="horizontal"
                                                tabSelected={selectedDay === index ? true : false}
                                                tabId={"weekDayTab" + index}
                                                onClick={() => {
                                                    setSelectedDay(index)
                                                }}
                                            ></TabPrimary>
                                        )
                                    })
                                }
                            </div>
                        </Col>
                    </Row>

                    {/** DEBUG formData - dev only */}
                    {/** formData && <pre>{JSON.stringify(formData, null, 2)}</pre>*/}

                    {formData &&
                        <CalendarSettingsFormDataInput
                            formData={formData[selectedDay]}
                            onTimeInputChange={onTimeInputChange}
                            onServiceTimeInputChange={onServiceTimeInputChange}
                        />
                    }
                </div></>}
        </div>

        {formData && !formError && <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.8rem", marginRight: "15px" }}>
            <Button
                buttonType="button"
                buttonUIVariant="primary"
                buttonSize="medium"
                buttonLabel={t('general.buttons.save', { ns: 'app' })}//"Salva"
                buttonIcon=""
                isDisabled={appUserRole===AppUserRole.READER}
                onClickEvent={(event: Event) => {
                    let errors: number = validateForm();
                    if (errors === 0) {
                        saveFormData();
                    }
                }}
            />
        </div>}

    </>
    );
}
export default CalendarSettingsForm;